/**
 * Review List Component
 * Displays contractor reviews with interactions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  Avatar,
  Button,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ThumbUp,
  Reply,
  MoreVert,
  Verified,
  Flag,
  Edit,
  Delete,
  Gavel
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import reviewService from '../../services/reviewService';
import ReviewAppealDialog from './ReviewAppealDialog';
import DialogCloseButton from '../common/DialogCloseButton';
import { formatDate, formatRelativeTime } from '../../utils/helpers';

const ReviewList = ({ contractorId, showAll = false, onReviewUpdate }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responseDialog, setResponseDialog] = useState({ open: false, reviewId: null });
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [appealDialog, setAppealDialog] = useState({ open: false, review: null });

  const { user } = useAuth();
  const isContractor = user?.id === contractorId;
  const isReviewee = user?.id === contractorId;

  useEffect(() => {
    loadReviews();
  }, [contractorId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const contractorReviews = await reviewService.getContractorReviews(contractorId);
      setReviews(showAll ? contractorReviews : contractorReviews.slice(0, 5));
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    try {
      await reviewService.markReviewHelpful(reviewId, user?.id);
      loadReviews(); // Refresh reviews
    } catch (error) {
      console.error('Error marking review helpful:', error);
    }
  };

  const handleAddResponse = async () => {
    if (!responseText.trim()) return;

    try {
      setSubmittingResponse(true);
      await reviewService.addContractorResponse(
        responseDialog.reviewId, 
        user?.id, 
        responseText.trim()
      );
      
      setResponseDialog({ open: false, reviewId: null });
      setResponseText('');
      loadReviews(); // Refresh reviews
      
      if (onReviewUpdate) {
        onReviewUpdate();
      }
    } catch (error) {
      console.error('Error adding response:', error);
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleMenuClick = (event, review) => {
    setMenuAnchor(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedReview(null);
  };

  const handleReportReview = async (reviewId, reason) => {
    try {
      await reviewService.reportReview(reviewId, reason, user?.id, selectedReview?.reviewType || 'user_review');
      handleMenuClose();
      // Show success message
    } catch (error) {
      console.error('Error reporting review:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await reviewService.deleteReview(reviewId, user?.id);
      loadReviews(); // Refresh reviews
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleFileAppeal = (review) => {
    setAppealDialog({ open: true, review });
    handleMenuClose();
  };

  const handleAppealFiled = () => {
    // Refresh reviews to show appeal status
    loadReviews();
    setAppealDialog({ open: false, review: null });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading reviews...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (reviews.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No reviews yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Be the first to review this contractor!
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {reviews.map((review, index) => (
        <Card key={review.id} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            {/* Review Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 40, height: 40 }}>
                  {(review.clientName || review.reviewerName || 'A').charAt(0)}
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {review.clientName}
                    </Typography>
                    {review.verified && (
                      <Verified color="primary" sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatRelativeTime(review.createdAt)} • {review.projectTitle}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rating value={review.rating} readOnly size="small" />
                <IconButton 
                  size="small" 
                  onClick={(e) => handleMenuClick(e, review)}
                >
                  <MoreVert />
                </IconButton>
              </Box>
            </Box>

            {/* Review Content */}
            <Typography variant="body1" paragraph>
              "{review.comment}"
            </Typography>

            {/* Contractor Response */}
            {review.response && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                borderLeft: '4px solid',
                borderColor: 'primary.main'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Response from contractor:
                </Typography>
                <Typography variant="body2" paragraph>
                  {review.response.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(review.response.date)}
                </Typography>
              </Box>
            )}

            {/* Review Actions */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => handleMarkHelpful(review.id)}
                  disabled={!user}
                >
                  Helpful ({review.helpful || 0})
                </Button>
                
                {isContractor && !review.response && (
                  <Button
                    size="small"
                    startIcon={<Reply />}
                    onClick={() => setResponseDialog({ open: true, reviewId: review.id })}
                  >
                    Respond
                  </Button>
                )}

                {isReviewee && !review.appeal_filed && (
                  <Button
                    size="small"
                    startIcon={<Gavel />}
                    onClick={() => handleFileAppeal(review)}
                    color="warning"
                  >
                    Appeal
                  </Button>
                )}

                {review.appeal_filed && (
                  <Chip 
                    label="Appeal Filed" 
                    size="small" 
                    color="warning"
                    icon={<Gavel />}
                  />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                {formatDate(review.date)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {isReviewee && !selectedReview?.appeal_filed && (
          <MenuItem onClick={() => handleFileAppeal(selectedReview)}>
            <Gavel sx={{ mr: 1 }} />
            File Appeal
          </MenuItem>
        )}
        {user?.id === selectedReview?.clientId && (
          <MenuItem onClick={() => handleDeleteReview(selectedReview.id)}>
            <Delete sx={{ mr: 1 }} />
            Delete Review
          </MenuItem>
        )}
        <MenuItem onClick={() => handleReportReview(selectedReview?.id, 'inappropriate')}>
          <Flag sx={{ mr: 1 }} />
          Report Review
        </MenuItem>
      </Menu>

      {/* Response Dialog */}
      <Dialog
        open={responseDialog.open}
        onClose={() => setResponseDialog({ open: false, reviewId: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setResponseDialog({ open: false, reviewId: null })} />
        <DialogTitle>Respond to Review</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your response will be visible to all users and shows your commitment to customer service.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Thank the client for their feedback and address any concerns professionally..."
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            variant="outlined"
            disabled={submittingResponse}
            inputProps={{ maxLength: 300 }}
            helperText={`${responseText.length}/300 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialog({ open: false, reviewId: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleAddResponse}
            disabled={submittingResponse || !responseText.trim()}
            variant="contained"
          >
            {submittingResponse ? 'Submitting...' : 'Submit Response'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appeal Dialog */}
      <ReviewAppealDialog
        open={appealDialog.open}
        onClose={() => setAppealDialog({ open: false, review: null })}
        review={appealDialog.review}
        onAppealFiled={handleAppealFiled}
      />
    </Box>
  );
};

export default ReviewList;
